import React, { useState } from 'react';
import { Trash2, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

export const CleanupPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [retentionDays, setRetentionDays] = useState(30);

  const handleCleanup = async () => {
    if (!confirm('Are you sure you want to delete old shared analyses?')) {
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.rpc('cleanup_old_analyses', {
        retention_days: retentionDays
      });

      if (error) throw error;

      setResult({
        success: true,
        message: `Successfully deleted ${data} old shared ${data === 1 ? 'analysis' : 'analyses'}`
      });
    } catch (err) {
      console.error('Error during cleanup:', err);
      setResult({
        success: false,
        message: 'Failed to clean up old analyses. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <Trash2 className="w-6 h-6 text-red-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Cleanup Shared Analyses
          </h2>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Delete old shared analyses that are no longer needed.
        </p>
      </div>

      <div className="p-6">
        <div className="max-w-xl">
          <div className="mb-6">
            <label htmlFor="retentionDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Retention Period (days)
            </label>
            <input
              type="number"
              id="retentionDays"
              value={retentionDays}
              onChange={(e) => setRetentionDays(Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Analyses older than this many days will be deleted
            </p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
                  Warning
                </h3>
                <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                  This action will permanently delete shared analyses older than {retentionDays} days. 
                  This cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {result && (
            <div className={`p-4 rounded-lg mb-6 ${
              result.success 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            }`}>
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <p className="text-sm">{result.message}</p>
              </div>
            </div>
          )}

          <button
            onClick={handleCleanup}
            disabled={isLoading}
            className="flex items-center justify-center w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Trash2 className="w-5 h-5 mr-2" />
                Clean Up Old Analyses
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};